
import { useState, useMemo, useRef } from 'react';
import Papa from 'papaparse';
import { useAuth } from '../../context/AuthContext';
import { createUsers, resetUserProgress, unrosterUser, moveStudent } from '../../api/admin';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { FiPlus, FiUsers, FiTrash2, FiRefreshCw, FiUserX, FiMail, FiArrowRight, FiUpload, FiDownload, FiX } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { TRACK_NAMES } from '../../types/constants';
import Logo from '../common/Logo';
import { StudentAccommodationsCard } from './StudentAccommodationsCard';

// Track options based on TRACK_NAMES constant - using only tracks 5-12 plus "No Track" option
const TRACKS = [
  { id: '', name: 'No Track' },
  { id: 'TRACK5', name: TRACK_NAMES.TRACK5 },
  { id: 'TRACK6', name: TRACK_NAMES.TRACK6 },
  { id: 'TRACK7', name: TRACK_NAMES.TRACK7 },
  { id: 'TRACK8', name: TRACK_NAMES.TRACK8 },
  { id: 'TRACK9', name: TRACK_NAMES.TRACK9 },
  { id: 'TRACK10', name: TRACK_NAMES.TRACK10 },
  { id: 'TRACK11', name: TRACK_NAMES.TRACK11 },
  { id: 'TRACK12', name: TRACK_NAMES.TRACK12 },
  { id: 'TRACK13', name: TRACK_NAMES.TRACK13 },
];

// Track options for Move Student - only actual tracks (no "No Track" or "ALL")
const MOVE_TRACKS = [
  // Pre-K tracks
  { id: 'TRACK_PREK_NAMING', name: TRACK_NAMES.TRACK_PREK_NAMING },
  { id: 'TRACK_PREK_SUBITIZING', name: TRACK_NAMES.TRACK_PREK_SUBITIZING },
  { id: 'TRACK_PREK_COUNTING', name: TRACK_NAMES.TRACK_PREK_COUNTING },
  { id: 'TRACK_PREK_WHATCOMESNEXT', name: TRACK_NAMES.TRACK_PREK_WHATCOMESNEXT },
  // K tracks
  { id: 'TRACK_K_NAMING', name: TRACK_NAMES.TRACK_K_NAMING },
  { id: 'TRACK_K_SUBITIZING', name: TRACK_NAMES.TRACK_K_SUBITIZING },
  { id: 'TRACK_K_COMPARING', name: TRACK_NAMES.TRACK_K_COMPARING },
  { id: 'TRACK_K_PARTWHOLE', name: TRACK_NAMES.TRACK_K_PARTWHOLE },
  { id: 'TRACK_K_ADDITION_WITHIN_5', name: TRACK_NAMES.TRACK_K_ADDITION_WITHIN_5 },
  { id: 'TRACK_K_ACCURACY', name: TRACK_NAMES.TRACK_K_ACCURACY },
  // Grade 1-5 tracks
  { id: 'TRACK5', name: TRACK_NAMES.TRACK5 },
  { id: 'TRACK6', name: TRACK_NAMES.TRACK6 },
  { id: 'TRACK7', name: TRACK_NAMES.TRACK7 },
  { id: 'TRACK8', name: TRACK_NAMES.TRACK8 },
  { id: 'TRACK9', name: TRACK_NAMES.TRACK9 },
  { id: 'TRACK10', name: TRACK_NAMES.TRACK10 },
  { id: 'TRACK11', name: TRACK_NAMES.TRACK11 },
  { id: 'TRACK12', name: TRACK_NAMES.TRACK12 },
  { id: 'TRACK13', name: TRACK_NAMES.TRACK13 },
];

// Access denied component for non-admin users
const AccessDenied = () => (
  <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col justify-center p-6">
    <div className="max-w-4xl mx-auto text-center">
      <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
      <p className="text-base text-gray-600 mt-2">You are not authorized to view this page.</p>
    </div>
  </div>
);

// Header component
const RosteringHeader = () => (
  <h1 className="text-4xl font-bold text-center pt-8 mb-6 flex items-center justify-center gap-4">
    <Logo size={32} />
    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
      User Rostering
    </span>
  </h1>
);

// Interface for user data
interface UserData {
  email: string;
  grade: number | ''; // Allow empty string for initial state, parse to number on change
  startingPoint: 'g1+' | 'prek';
}

// Interface for API response
interface UserCreationResult {
  email: string;
  userId?: string;
  status: string;
  oneRosterStatus?: string;
  message?: string;
}

interface ApiResponse {
  success: boolean;
  message: string;
  results?: {
    totalProcessed: number;
    created: number;
    alreadyExisting: number;
    failed: number;
    details: UserCreationResult[];
  };
}

// Parsed row from CSV with per-row validation errors
interface ParsedRow {
  email: string;
  grade: string;
  startingPoint: string;
  validationErrors: string[];
}

// Sub-component that renders the API response grid + details table
// Used by both the manual form and the bulk CSV flow
const RosteringResultDisplay = ({ response }: { response: ApiResponse }) => (
  <div className={`p-4 rounded-lg ${
    response.success
      ? 'bg-green-50 border border-green-200 text-green-800'
      : 'bg-red-50 border border-red-200 text-red-800'
  }`}>
    <div className="font-medium mb-2">{response.message}</div>

    {response.results && (
      <div className="space-y-4 mt-4">
        <div className="grid grid-cols-4 gap-2 text-sm">
          <div className="bg-white bg-opacity-50 p-2 rounded text-center">
            <div className="font-medium">Total</div>
            <div className="text-xl font-bold">{response.results.totalProcessed}</div>
          </div>
          <div className="bg-green-100 p-2 rounded text-center">
            <div className="font-medium">Created</div>
            <div className="text-xl font-bold text-green-700">{response.results.created}</div>
          </div>
          <div className="bg-blue-100 p-2 rounded text-center">
            <div className="font-medium">Existing</div>
            <div className="text-xl font-bold text-blue-700">{response.results.alreadyExisting}</div>
          </div>
          <div className="bg-red-100 p-2 rounded text-center">
            <div className="font-medium">Failed</div>
            <div className="text-xl font-bold text-red-700">{response.results.failed}</div>
          </div>
        </div>

        {response.results.details && response.results.details.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User ID</th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {response.results.details.map((detail, idx) => (
                  <tr key={idx} className="text-sm">
                    <td className="px-3 py-2 whitespace-nowrap">{detail.email}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        detail.status === 'created'
                          ? 'bg-green-100 text-green-800'
                          : detail.status === 'existing'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-red-100 text-red-800'
                      }`}>
                        {detail.status}
                      </span>
                      {detail.oneRosterStatus && (
                        <span className="ml-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          OneRoster: {detail.oneRosterStatus}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{detail.userId || '-'}</td>
                    <td className="px-3 py-2">{detail.message || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    )}
  </div>
);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CHUNK_SIZE = 25;

function validateBulkRows(rawRows: Record<string, string>[]): ParsedRow[] {
  // First pass: build rows with field-level errors
  const rows: ParsedRow[] = rawRows.map(raw => {
    const email = (raw['email'] ?? '').trim();
    const grade = (raw['grade'] ?? '').trim();
    const startingPoint = (raw['startingpoint'] ?? '').trim().toLowerCase();
    const errors: string[] = [];

    if (!email) {
      errors.push('Email is required');
    } else if (!EMAIL_REGEX.test(email)) {
      errors.push('Invalid email format');
    }

    const gradeNum = parseInt(grade, 10);
    if (grade === '') {
      errors.push('Grade is required');
    } else if (isNaN(gradeNum) || gradeNum < -1) {
      errors.push('Grade must be an integer >= -1');
    }

    if (startingPoint !== '' && startingPoint !== 'g1+' && startingPoint !== 'prek') {
      errors.push('Starting point must be "g1+", "prek", or blank');
    }

    return { email, grade, startingPoint, validationErrors: errors };
  });

  // Second pass: flag duplicate emails within the file
  const seenEmails = new Map<string, number>();
  rows.forEach((row, idx) => {
    const key = row.email.toLowerCase();
    if (key && EMAIL_REGEX.test(key)) {
      if (seenEmails.has(key)) {
        rows[idx].validationErrors.push('Duplicate email in file');
      } else {
        seenEmails.set(key, idx);
      }
    }
  });

  return rows;
}

export const RosteringPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserData[]>([{ email: '', grade: '', startingPoint: 'g1+' }]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiResponse, setApiResponse] = useState<ApiResponse | null>(null);

  // State for progress reset form
  const [resetEmail, setResetEmail] = useState('');
  const [resetTrackId, setResetTrackId] = useState(TRACKS[0].id);
  const [isResetting, setIsResetting] = useState(false);
  const [resetResponse, setResetResponse] = useState<{
    success?: boolean;
    message: string;
  } | null>(null);

  // State for unroster form
  const [unrosterEmail, setUnrosterEmail] = useState('');
  const [isUnrostering, setIsUnrostering] = useState(false);
  const [unrosterResponse, setUnrosterResponse] = useState<{
    success?: boolean;
    message: string;
  } | null>(null);

  // State for move student form
  const [moveEmail, setMoveEmail] = useState('');
  const [moveAgeGrade, setMoveAgeGrade] = useState<string>('');
  const [moveFocusTrack, setMoveFocusTrack] = useState<string>('');
  const [isMoving, setIsMoving] = useState(false);
  const [moveResponse, setMoveResponse] = useState<{
    success?: boolean;
    message: string;
    enrollmentResult?: {
      success: boolean;
      message: string;
      transferredFrom?: string;
      transferredTo?: string;
    };
  } | null>(null);

  // Bulk CSV state
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkRows, setBulkRows] = useState<ParsedRow[]>([]);
  const [bulkParseError, setBulkParseError] = useState<string | null>(null);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ current: number; total: number } | null>(null);
  const [bulkResponse, setBulkResponse] = useState<ApiResponse | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if user is admin - same logic as AdminPage
  const isAdmin = useMemo(() =>
    currentUser?.role === 'admin',
    [currentUser?.role]
  );

  // Add a new empty user field
  const addUser = () => {
    setUsers([...users, { email: '', grade: '', startingPoint: 'g1+' }]);
  };

  // Remove a user at specific index
  const removeUser = (index: number) => {
    const newUsers = [...users];
    newUsers.splice(index, 1);
    setUsers(newUsers);
  };

  // Update user field
  const updateUserField = (index: number, field: keyof UserData, value: string) => {
    const newUsers = [...users];
    if (field === 'grade') {
      newUsers[index][field] = value === '' ? '' : parseInt(value, 10);
    } else if (field === 'startingPoint') {
      newUsers[index][field] = value as 'g1+' | 'prek';
    } else {
      newUsers[index][field] = value;
    }
    setUsers(newUsers);
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate form
    const invalidUsers = users.filter(user => !user.email || user.grade === '' || isNaN(Number(user.grade)));
    if (invalidUsers.length > 0) {
      setApiResponse({
        success: false,
        message: 'Please fill in all required fields for all users (email, grade).'
      });
      return;
    }

    setIsSubmitting(true);
    setApiResponse(null);

    try {
      // Call the API - map startingPoint to trackId for backend
      const usersToSubmit = users.map(user => ({
        email: user.email,
        grade: Number(user.grade),
        trackId: user.startingPoint === 'prek' ? 'TRACK_PREK_NAMING' : '',
      }));
      const response = await createUsers(usersToSubmit);

      // Set the API response for display
      setApiResponse(response);

      // Reset form on success
      if (response.success) {
        setUsers([{ email: '', grade: '', startingPoint: 'g1+' }]);
      }
    } catch (error) {
      // Handle error
      setApiResponse({
        success: false,
        message: error instanceof Error ? error.message : 'An unexpected error occurred.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle user progress reset
  const handleProgressReset = async () => {
    if (!resetEmail || !resetTrackId) {
      setResetResponse({ success: false, message: 'Please enter an email address and select a track.' });
      return;
    }

    // Show confirmation dialog
    if (!window.confirm(`Are you sure you want to reset progress for track ${resetTrackId} for user ${resetEmail}? This action cannot be undone.`)) {
      return;
    }

    setIsResetting(true);
    setResetResponse(null);

    try {
      const response = await resetUserProgress(resetEmail, resetTrackId);
      setResetResponse(response);
      if (response.success) {
        setResetEmail('');
      }
    } catch (error) {
      setResetResponse({
        success: false,
        message: error instanceof Error ? error.message : 'An unexpected error occurred.',
      });
    } finally {
      setIsResetting(false);
    }
  };

  // Handle user unrostering
  const handleUnroster = async () => {
    if (!unrosterEmail.trim()) {
      setUnrosterResponse({ success: false, message: 'Please enter an email address.' });
      return;
    }

    setIsUnrostering(true);
    setUnrosterResponse(null);

    try {
      const response = await unrosterUser(unrosterEmail.trim());
      setUnrosterResponse(response);
      if (response.success) {
        setUnrosterEmail('');
      }
    } catch (error) {
      setUnrosterResponse({
        success: false,
        message: error instanceof Error ? error.message : 'An unexpected error occurred.',
      });
    } finally {
      setIsUnrostering(false);
    }
  };

  // Handle move student
  const handleMoveStudent = async () => {
    if (!moveEmail.trim()) {
      setMoveResponse({ success: false, message: 'Please enter an email address.' });
      return;
    }

    if (!moveAgeGrade && !moveFocusTrack) {
      setMoveResponse({ success: false, message: 'Please provide at least one field to update (grade or track).' });
      return;
    }

    // Build confirmation message
    const updates = [];
    if (moveAgeGrade) updates.push(`grade to ${moveAgeGrade}`);
    if (moveFocusTrack) updates.push(`track to ${moveFocusTrack}`);

    if (!window.confirm(`Are you sure you want to update ${updates.join(' and ')} for ${moveEmail}? This will also update their OneRoster enrollments.`)) {
      return;
    }

    setIsMoving(true);
    setMoveResponse(null);

    try {
      const response = await moveStudent(
        moveEmail.trim(),
        moveAgeGrade ? parseInt(moveAgeGrade, 10) : undefined,
        moveFocusTrack || undefined
      );
      setMoveResponse(response);
      if (response.success) {
        setMoveEmail('');
        setMoveAgeGrade('');
        setMoveFocusTrack('');
      }
    } catch (error) {
      setMoveResponse({
        success: false,
        message: error instanceof Error ? error.message : 'An unexpected error occurred.',
      });
    } finally {
      setIsMoving(false);
    }
  };

  // Download CSV template
  const handleDownloadTemplate = () => {
    const content = 'email,grade,startingPoint\nstudent1@example.com,5,g1+\nstudent2@example.com,-1,prek\n';
    const blob = new Blob([content], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk-rostering-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Parse uploaded CSV file
  const processBulkFile = (file: File | null) => {
    setBulkFile(file);
    setBulkRows([]);
    setBulkParseError(null);
    setBulkResponse(null);
    setBulkProgress(null);

    if (!file) return;

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h: string) => h.trim().toLowerCase(),
      complete: (results) => {
        if (results.errors && results.errors.length > 0) {
          setBulkParseError(`CSV parse error: ${results.errors[0].message}`);
          return;
        }

        const fields = results.meta.fields ?? [];
        const requiredHeaders = ['email', 'grade', 'startingpoint'];
        const missing = requiredHeaders.filter(h => !fields.includes(h));
        if (missing.length > 0) {
          const displayNames: Record<string, string> = { email: 'email', grade: 'grade', startingpoint: 'startingPoint' };
          setBulkParseError(`Missing required column(s): ${missing.map(h => displayNames[h]).join(', ')}`);
          return;
        }

        if (results.data.length === 0) {
          setBulkParseError('No rows found in CSV.');
          return;
        }

        setBulkRows(validateBulkRows(results.data));
      },
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    processBulkFile(e.target.files?.[0] ?? null);
  };

  // Clear the uploaded file and reset bulk state
  const handleClearFile = () => {
    setBulkFile(null);
    setBulkRows([]);
    setBulkParseError(null);
    setBulkResponse(null);
    setBulkProgress(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Submit bulk CSV rows in chunks of 25, sequentially
  const handleBulkSubmit = async () => {
    const validRows = bulkRows.filter(r => r.validationErrors.length === 0);
    if (validRows.length === 0) return;

    if (!window.confirm(`Submit ${validRows.length} users? This will create accounts and OneRoster enrollments.`)) {
      return;
    }

    const payload = validRows.map(row => ({
      email: row.email.trim().toLowerCase(),
      grade: parseInt(row.grade, 10),
      trackId: row.startingPoint === 'prek' ? 'TRACK_PREK_NAMING' : '',
    }));

    const chunks: typeof payload[] = [];
    for (let i = 0; i < payload.length; i += CHUNK_SIZE) {
      chunks.push(payload.slice(i, i + CHUNK_SIZE));
    }

    setIsBulkSubmitting(true);
    setBulkResponse(null);
    setBulkProgress({ current: 0, total: chunks.length });

    const aggregated: ApiResponse = {
      success: true,
      message: '',
      results: {
        totalProcessed: 0,
        created: 0,
        alreadyExisting: 0,
        failed: 0,
        details: [],
      },
    };

    for (let i = 0; i < chunks.length; i++) {
      setBulkProgress({ current: i + 1, total: chunks.length });
      try {
        const res = await createUsers(chunks[i]);
        if (res.results) {
          aggregated.results!.totalProcessed += res.results.totalProcessed;
          aggregated.results!.created += res.results.created;
          aggregated.results!.alreadyExisting += res.results.alreadyExisting;
          aggregated.results!.failed += res.results.failed;
          aggregated.results!.details.push(...res.results.details);
        }
        if (!res.success) {
          aggregated.success = false;
        }
      } catch (error) {
        // Record all users in failed chunk as failed; continue with next chunk
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        aggregated.success = false;
        aggregated.results!.totalProcessed += chunks[i].length;
        aggregated.results!.failed += chunks[i].length;
        aggregated.results!.details.push(
          ...chunks[i].map(u => ({
            email: u.email,
            status: 'failed',
            message: errorMessage,
          }))
        );
      }
    }

    const totalCreated = aggregated.results!.created;
    const totalFailed = aggregated.results!.failed;
    const totalExisting = aggregated.results!.alreadyExisting;
    aggregated.message = `Bulk import complete: ${totalCreated} created, ${totalExisting} already existing, ${totalFailed} failed.`;
    if (totalFailed > 0) {
      aggregated.success = false;
    }

    setBulkResponse(aggregated);
    setBulkProgress(null);
    setIsBulkSubmitting(false);
  };

  const bulkValidCount = bulkRows.filter(r => r.validationErrors.length === 0).length;
  const bulkInvalidCount = bulkRows.filter(r => r.validationErrors.length > 0).length;
  const bulkSubmitDisabled =
    !bulkFile ||
    !!bulkParseError ||
    bulkRows.length === 0 ||
    bulkInvalidCount > 0 ||
    isBulkSubmitting;

  // Early return if not admin
  if (!isAdmin) {
    return <AccessDenied />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-6">
      <div className="max-w-5xl mx-auto space-y-8">
        <RosteringHeader />

        {/* Navigation */}
        <div className="flex mb-4">
          <Link
            to="/admin"
            className="text-blue-600 hover:text-blue-800 transition-colors duration-300"
          >
            ← Back to Admin Dashboard
          </Link>
        </div>

        <StudentAccommodationsCard />

        {/* User Rostering Form */}
        <Card className="overflow-hidden rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-4 px-6">
            <h2 className="text-2xl font-semibold text-white flex items-center gap-2">
              <FiUsers className="text-yellow-300" size={24} />
              Add New Users
            </h2>
          </div>
          <CardContent className="p-6 space-y-6">
            {/* Bulk CSV link notice */}
            <p className="text-sm text-gray-500">
              Adding many students?{' '}
              <a
                href="#bulk-add-users"
                className="text-blue-600 hover:text-blue-800 font-medium"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('bulk-add-users')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Use Bulk Add Users (CSV) below →
              </a>
            </p>

            {/* API Response Display */}
            {apiResponse && <RosteringResultDisplay response={apiResponse} />}

            {/* User forms */}
            <div className="space-y-4">
              {users.map((user, index) => (
                <div key={index} className="p-4 rounded-lg border border-gray-200 space-y-4">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-medium">User {index + 1}</h3>
                    {users.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeUser(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <FiTrash2 size={16} />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor={`email-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <Input
                        id={`email-${index}`}
                        type="email"
                        placeholder="student@example.com"
                        value={user.email}
                        onChange={(e) => updateUserField(index, 'email', e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label htmlFor={`grade-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                        Grade
                      </label>
                      <Input
                        id={`grade-${index}`}
                        type="number"
                        min="-1"
                        placeholder="5"
                        value={user.grade !== undefined ? user.grade : ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          updateUserField(index, 'grade', value);
                        }}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label htmlFor={`starting-point-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                        Starting Point
                      </label>
                      <select
                        id={`starting-point-${index}`}
                        value={user.startingPoint}
                        onChange={(e) => {
                          updateUserField(index, 'startingPoint', e.target.value);
                        }}
                        className="w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      >
                        <option value="g1+">G1+</option>
                        <option value="prek">Pre-K</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Add row button */}
            <div className="flex justify-center">
              <Button
                onClick={addUser}
                variant="outline"
                className="flex items-center gap-2"
              >
                <FiPlus size={16} />
                Add Another User
              </Button>
            </div>

            {/* Submit button */}
            <div className="text-center">
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-6 py-2 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-lg font-bold text-base shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2 mx-auto"
              >
                {isSubmitting ? 'Submitting...' : (
                  <>
                    <FiRefreshCw size={16} />
                    Submit
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Add Users (CSV) Card */}
        <Card id="bulk-add-users" className="overflow-hidden rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <div className="bg-gradient-to-r from-emerald-600 to-cyan-600 py-4 px-6">
            <h2 className="text-2xl font-semibold text-white flex items-center gap-2">
              <FiUpload className="text-white" size={24} />
              Bulk Add Users (CSV)
            </h2>
          </div>
          <CardContent className="p-6 space-y-6">
            {/* Template download */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <p className="text-sm text-gray-600">
                Upload a CSV with columns: <span className="font-mono text-xs bg-gray-100 px-1 py-0.5 rounded">email</span>,{' '}
                <span className="font-mono text-xs bg-gray-100 px-1 py-0.5 rounded">grade</span>,{' '}
                <span className="font-mono text-xs bg-gray-100 px-1 py-0.5 rounded">startingPoint</span> (g1+ or prek).
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadTemplate}
                className="flex items-center gap-2 shrink-0"
              >
                <FiDownload size={14} />
                Download template
              </Button>
            </div>

            {/* File upload area */}
            <div>
              {!bulkFile ? (
                <label
                  className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                    isDragging
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                  }`}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                  onDrop={(e) => { e.preventDefault(); setIsDragging(false); const file = e.dataTransfer.files?.[0] ?? null; if (file) { processBulkFile(file); } }}
                >
                  <div className="flex flex-col items-center gap-2 text-gray-500">
                    <FiUpload size={24} />
                    <span className="text-sm font-medium">Click to upload CSV</span>
                    <span className="text-xs">or drag and drop</span>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv,text/plain,application/vnd.ms-excel"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </label>
              ) : (
                <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-center gap-2 text-emerald-800">
                    <FiUpload size={16} />
                    <span className="text-sm font-medium">{bulkFile.name}</span>
                    <span className="text-xs text-emerald-600">({(bulkFile.size / 1024).toFixed(1)} KB)</span>
                  </div>
                  <button
                    onClick={handleClearFile}
                    className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded"
                    aria-label="Remove file"
                  >
                    <FiX size={16} />
                  </button>
                </div>
              )}
            </div>

            {/* Top-level parse error banner */}
            {bulkParseError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm font-medium">
                {bulkParseError}
              </div>
            )}

            {/* Preview table */}
            {bulkRows.length > 0 && (
              <div className="space-y-4">
                {/* Counts summary */}
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="bg-gray-100 p-2 rounded text-center">
                    <div className="font-medium text-gray-600">Total</div>
                    <div className="text-xl font-bold text-gray-800">{bulkRows.length}</div>
                  </div>
                  <div className="bg-green-100 p-2 rounded text-center">
                    <div className="font-medium text-green-700">Valid</div>
                    <div className="text-xl font-bold text-green-700">{bulkValidCount}</div>
                  </div>
                  <div className="bg-red-100 p-2 rounded text-center">
                    <div className="font-medium text-red-700">Invalid</div>
                    <div className="text-xl font-bold text-red-700">{bulkInvalidCount}</div>
                  </div>
                </div>

                {/* Row preview table */}
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Starting Point</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {bulkRows.map((row, idx) => {
                        const isInvalid = row.validationErrors.length > 0;
                        return (
                          <tr key={idx} className={isInvalid ? 'bg-red-50' : ''}>
                            <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{idx + 1}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{row.email || <span className="text-gray-400 italic">empty</span>}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{row.grade || <span className="text-gray-400 italic">empty</span>}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {row.startingPoint ? (
                                row.startingPoint
                              ) : (
                                <span className="text-gray-400 italic">g1+ (default)</span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {isInvalid ? (
                                <span className="text-red-600 text-xs">{row.validationErrors.join('; ')}</span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Valid
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Progress indicator */}
            {isBulkSubmitting && bulkProgress && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800 text-sm">
                Submitting batch {bulkProgress.current} of {bulkProgress.total}...{' '}
                ({Math.min(bulkProgress.current * CHUNK_SIZE, bulkValidCount)} of {bulkValidCount} users processed)
              </div>
            )}

            {/* Bulk API response */}
            {bulkResponse && <RosteringResultDisplay response={bulkResponse} />}

            {/* Submit button */}
            <div className="text-center">
              <Button
                onClick={handleBulkSubmit}
                disabled={bulkSubmitDisabled}
                className="px-6 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white rounded-lg font-bold text-base shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2 mx-auto disabled:opacity-50 disabled:pointer-events-none"
              >
                {isBulkSubmitting ? (
                  <>
                    <FiRefreshCw size={16} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <FiUpload size={16} />
                    Submit {bulkValidCount > 0 ? `${bulkValidCount} Users` : 'CSV'}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Reset User Progress Card */}
        <Card className="overflow-hidden rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <div className="bg-gradient-to-r from-red-600 to-yellow-600 py-4 px-6">
            <h2 className="text-2xl font-semibold text-white flex items-center gap-2">
              <FiTrash2 className="text-white" size={24} />
              Reset User Track Progress
            </h2>
          </div>
          <CardContent className="p-6 space-y-6">
            {/* Reset Progress Response Display */}
            {resetResponse && (
              <div className={`p-4 rounded-lg ${
                resetResponse.success
                  ? "bg-green-50 border border-green-200 text-green-800"
                  : "bg-red-50 border border-red-200 text-red-800"
              }`}>
                <div className="font-medium">{resetResponse.message}</div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1">
                  User Email
                </label>
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="student@example.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <label htmlFor="reset-track" className="block text-sm font-medium text-gray-700 mb-1">
                  Track to Reset
                </label>
                <select
                  id="reset-track"
                  value={resetTrackId}
                  onChange={(e) => setResetTrackId(e.target.value)}
                  className="w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  {TRACKS.map(track => (
                    <option key={track.id} value={track.id}>
                      {track.id} - {track.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-sm text-gray-500 text-center">
              This will delete all progress data for a user for the selected track. This action is irreversible.
            </p>

            <div className="text-center">
              <Button
                onClick={handleProgressReset}
                disabled={isResetting}
                className="px-6 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg font-bold text-base shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2 mx-auto"
              >
                {isResetting ? 'Resetting...' : (
                  <>
                    <FiTrash2 size={16} />
                    Reset Track Progress
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Unroster User Card */}
        <Card className="overflow-hidden rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <div className="bg-gradient-to-r from-red-600 to-pink-600 py-4 px-6">
            <h2 className="text-2xl font-semibold text-white flex items-center gap-2">
              <FiUserX className="text-white" size={24} />
              Unroster Student
            </h2>
          </div>
          <CardContent className="p-6 space-y-6">
            {/* Unroster Response Display */}
            {unrosterResponse && (
              <div className={`p-4 rounded-lg ${
                unrosterResponse.success
                  ? "bg-green-50 border border-green-200 text-green-800"
                  : "bg-red-50 border border-red-200 text-red-800"
              }`}>
                <div className="font-medium">{unrosterResponse.message}</div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label htmlFor="unroster-email" className="block text-sm font-medium text-gray-700 mb-2">
                  Student Email
                </label>
                <div className="relative">
                  <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    id="unroster-email"
                    type="email"
                    placeholder="Enter student email address"
                    value={unrosterEmail}
                    onChange={(e) => setUnrosterEmail(e.target.value)}
                    className="w-full pl-10"
                  />
                </div>
              </div>
            </div>

            <div className="p-3 bg-yellow-50 rounded-md">
              <p className="text-xs text-yellow-800">
                <strong>Warning:</strong> This action will prevent the student from accessing their account. Use with caution.
              </p>
            </div>

            <div className="text-center">
              <Button
                onClick={handleUnroster}
                disabled={isUnrostering || !unrosterEmail.trim()}
                className="px-6 py-2 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-lg font-bold text-base shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2 mx-auto"
              >
                {isUnrostering ? 'Unrostering...' : (
                  <>
                    <FiUserX size={16} />
                    Unroster Student
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Move Student Card */}
        <Card className="overflow-hidden rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 py-4 px-6">
            <h2 className="text-2xl font-semibold text-white flex items-center gap-2">
              <FiArrowRight className="text-white" size={24} />
              Move Student
            </h2>
          </div>
          <CardContent className="p-6 space-y-6">
            {/* Move Response Display */}
            {moveResponse && (
              <div className={`p-4 rounded-lg ${
                moveResponse.success
                  ? "bg-green-50 border border-green-200 text-green-800"
                  : "bg-red-50 border border-red-200 text-red-800"
              }`}>
                <div className="font-medium">{moveResponse.message}</div>
                {moveResponse.enrollmentResult && (
                  <div className="mt-2 text-sm">
                    {moveResponse.enrollmentResult.transferredFrom && (
                      <div>From: {moveResponse.enrollmentResult.transferredFrom}</div>
                    )}
                    {moveResponse.enrollmentResult.transferredTo && (
                      <div>To: {moveResponse.enrollmentResult.transferredTo}</div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="move-email" className="block text-sm font-medium text-gray-700 mb-2">
                  Student Email
                </label>
                <div className="relative">
                  <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    id="move-email"
                    type="email"
                    placeholder="student@example.com"
                    value={moveEmail}
                    onChange={(e) => setMoveEmail(e.target.value)}
                    className="w-full pl-10"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="move-grade" className="block text-sm font-medium text-gray-700 mb-2">
                  New Age Grade (optional)
                </label>
                <Input
                  id="move-grade"
                  type="number"
                  min="-1"
                  placeholder="e.g., 3"
                  value={moveAgeGrade}
                  onChange={(e) => setMoveAgeGrade(e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <label htmlFor="move-track" className="block text-sm font-medium text-gray-700 mb-2">
                  New Focus Track (optional)
                </label>
                <select
                  id="move-track"
                  value={moveFocusTrack}
                  onChange={(e) => setMoveFocusTrack(e.target.value)}
                  className="w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  <option value="">Select a track</option>
                  {MOVE_TRACKS.map(track => (
                    <option key={track.id} value={track.id}>
                      {track.id} - {track.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="p-3 bg-blue-50 rounded-md">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> Updating the focus track will end current OneRoster enrollments and create a new enrollment for the target grade.
              </p>
            </div>

            <div className="text-center">
              <Button
                onClick={handleMoveStudent}
                disabled={isMoving || !moveEmail.trim() || (!moveAgeGrade && !moveFocusTrack)}
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg font-bold text-base shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2 mx-auto"
              >
                {isMoving ? 'Moving...' : (
                  <>
                    <FiArrowRight size={16} />
                    Move Student
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RosteringPage;
